import { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  BucketAwtENUM,
  EstadosOrdenTrabajoEnumChoice,
  ModulosEnumChoice,
  ROUTER_PATHS,
  TipoParametroChoiceEnum,
  gridSizeMdLg6,
  reportarNovedadOTSchema,
  useRecordDirectLock,
  useUploadFile,
  useUploadImagesGeneric,
} from '@/shared';
import {
  useFetchMotivos,
  useFetchMotivosRechazo,
  useUpdateOrdenTrabajo,
} from '@/shared/actions/erp';
import { RejectOTByTecnicoData } from '@/shared/actions/erp/tecnico/instalaciones/asignaciones.actions';
import {
  CustomAutocomplete,
  CustomTextArea,
  TabTextLabel,
} from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import {
  AlertSeverityEnum,
  DiccionarioMotivo,
  EstadoBitacoraEnum,
  EstadoMotivoEnum,
  IDataBitacoraRespById,
  Motivo,
  MotivoRechazoOrdenTrabajo,
  OrdenTrabajo,
  Venta,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { useDiccionarioMotivo } from '@/store/erp/diccionario_motivo/diccionario_motivo.store';
import { useAuthStore } from '@/store/auth';
import { esOrdenTraslado } from './fotos/traslado/fotos-traslado.utils';
import { toast } from 'react-toastify';

export interface ConfirmRejectOTDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  idOrdenTrabajo: string;
  observacionesOrdenTrabajo?: Record<string, string>;
  ordentrabajo?: OrdenTrabajo;
}

type RechazoFormData = RejectOTByTecnicoData & {
  observacion_rechazo_tecnico: string;
  estado_informe: string;
  categoria_rechazo: string;
};

// --- Constantes para el flujo en cascada ---
type EstadoOption = {
  nombre: string;
  codigo: string;
};

const ESTADO_OPTIONS: EstadoOption[] = [
  { nombre: 'Fallido', codigo: 'FALLA' },
  { nombre: 'Re coordinar', codigo: 'RE_COORDINAR' },
];

const CATEGORIA_LABELS: Record<string, string> = {
  CLIENTE: 'Cliente',
  NO_ACCESIBLE: 'No Accesible',
};

const RejectInstalacionAsigTecModal: React.FC<ConfirmRejectOTDialogProps> = ({
  open,
  idOrdenTrabajo,
  setOpen,
  observacionesOrdenTrabajo,
  ordentrabajo,
}) => {
  const navigate = useNavigate();
  const returnUrlInstalacionesTecnicoPage =
    ROUTER_PATHS.tecnico.ordenenTrabajoNav;
  // Traslado: mismo flujo/endpoint que instalación; solo etiquetamos la bitácora
  // para que "diga TRASLADO" (el backend enruta idéntico). No afecta instalación.
  const esTraslado = esOrdenTraslado(ordentrabajo);
  const { releaseLock } = useRecordDirectLock(idOrdenTrabajo);

  ///* global state
  const setAlert = useUiStore(s => s.setAlert);
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

  ///* socket & auth for bitacora auto-creation
  const socket = useDiccionarioMotivo(s => s.socket);
  const user = useAuthStore(s => s.user);

  ///* local state for cascading
  const [selectedEstado, setSelectedEstado] = useState<string>('');
  const [selectedCategoria, setSelectedCategoria] = useState<string>('');

  ///* form
  const formRechazo = useForm<RechazoFormData>({
    resolver: yupResolver(reportarNovedadOTSchema) as any,
    defaultValues: {
      estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.PRE_RECHAZADO,
    },
  });

  const {
    UploadImageComponent,
    image1: fotoRechazoTecnico1,
    setImage1: setFotoRechazoTecnico1,
    image2: fotoRechazoTecnico2,
    setImage2: setFotoRechazoTecnico2,
  } = useUploadImagesGeneric();

  const { handleUploadFile } = useUploadFile();

  ///* fetch data
  const { data: motivosRechazoOT, isLoading: isLoadingMotivosRechazoOT } =
    useFetchMotivosRechazo({
      page_size: 100,
      modulo: ModulosEnumChoice.ORDEN_TRABAJO,
    });

  ///* derived cascading options
  const motivosConAuxiliares = useMemo(() => {
    const data =
      (motivosRechazoOT?.data?.data as unknown as MotivoRechazoOrdenTrabajo[]) || [];
    return data.filter(m => m.auxiliares?.estado && m.auxiliares?.categoria);
  }, [motivosRechazoOT]);

  const categoriaOptions = useMemo(() => {
    if (!selectedEstado) return [];
    const cats = [
      ...new Set(
        motivosConAuxiliares
          .filter(m => m.auxiliares?.estado === selectedEstado)
          .map(m => m.auxiliares!.categoria!)
      ) as any,
    ];
    return cats.map(cat => ({
      nombre: CATEGORIA_LABELS[cat] || cat,
      codigo: cat,
    }));
  }, [selectedEstado, motivosConAuxiliares]);

  const subcategoriaOptions = useMemo(() => {
    if (!selectedEstado || !selectedCategoria) return [];
    return motivosConAuxiliares.filter(
      m =>
        m.auxiliares?.estado === selectedEstado &&
        m.auxiliares?.categoria === selectedCategoria
    );
  }, [selectedEstado, selectedCategoria, motivosConAuxiliares]);

  ///* fetch bitacora motivos for auto-creation
  const { data: dataMotivos } = useFetchMotivos({
    estado: true,
    modulo_in: 'BITACORA PYL',
    page_size: 256,
  });

  ///* mutations (no redirect/alert - handled manually after bitacora creation)
  const rejectOrdenTrabajo = useUpdateOrdenTrabajo<RejectOTByTecnicoData>({
    enableRedirect: false,
    enableSuccessAlert: false,
  });

  ///* handlers
  const handleResetForm = () => {
    setOpen(false);
    formRechazo.reset();
    setSelectedEstado('');
    setSelectedCategoria('');
    setFotoRechazoTecnico1(null);
    setFotoRechazoTecnico2(null);
  };

  const onSaveRejection = async (data: RechazoFormData) => {
    if (isLoadingMotivosRechazoOT) return;

    handleResetForm();
    setIsGlobalLoading(true);

    const [fotoRechazoTecnico1Data, fotoRechazoTecnico2Data] =
      await Promise.all([
        handleUploadFile(fotoRechazoTecnico1!, BucketAwtENUM.IMAGE_INSTALACION),
        handleUploadFile(fotoRechazoTecnico2!, BucketAwtENUM.IMAGE_INSTALACION),
      ]);

    const esFalla = data.estado_informe === 'FALLA';

    try {
      await rejectOrdenTrabajo.mutateAsync({
        id: idOrdenTrabajo,
        data: {
          estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.RECHAZADA,
          recoordination_required: true,
          tipo_novedad: esFalla ? 'FALLA' : 'RE_COORDINAR',
          motivo_rechazo: data.motivo_rechazo,
          observaciones: {
            ...observacionesOrdenTrabajo,
            observacion_rechazo_tecnico: data.observacion_rechazo_tecnico,
          },
          urls_fotos_rechazo_tecnico: [
            {
              photoName: 'Foto Rechazo Tecnico 1',
              photoId: fotoRechazoTecnico1Data.id_archivos,
              photoUrl: fotoRechazoTecnico1Data.url || '',
            },
            {
              photoName: 'Foto Rechazo Tecnico 2',
              photoId: fotoRechazoTecnico2Data.id_archivos,
              photoUrl: fotoRechazoTecnico2Data.url || '',
            },
          ],
        },
      }, {
        onSuccess: () => {
          releaseLock();
        },
      });

      // Auto-create bitacora via socket
      if (socket && ordentrabajo && dataMotivos?.data?.data) {
        const motivoCodigo = esFalla
          ? 'FALLIDO_PLANTA_EXTERNA_PYL'
          : 'RECORDINADO_PLANTA_EXTERNA_PYL';
        const motivoData = dataMotivos.data.data.find(
          (m: any) => m.codigo === motivoCodigo
        );
        const motivoRechazoNombre =
          motivosConAuxiliares.find(m => m.codigo === data.motivo_rechazo)
            ?.nombre || '';

        if (motivoData) {
          const idPreagendamiento =
            ordentrabajo.preagendamiento_data?.id_preagendamiento ||
            ordentrabajo.preagendamiento;

          const textoComentarioBase = motivoRechazoNombre
            ? `Motivo: ${motivoRechazoNombre} - ${data.observacion_rechazo_tecnico}`
            : data.observacion_rechazo_tecnico;
          // Etiqueta TRASLADO en la bitácora para diferenciar de instalación.
          const textoComentario = esTraslado
            ? `TRASLADO — ${textoComentarioBase}`
            : textoComentarioBase;

          const ventaData = {
            numero_referencia_venta: idPreagendamiento,
            identificacion_cliente:
              ordentrabajo.prospecto_data?.identificacion,
            nombre_cliente: ordentrabajo.prospecto_data?.razon_social,
            canal_venta: ordentrabajo.prospecto_data?.canal_venta_create,
            vendedor: ordentrabajo.prospecto_data?.user_create,
            fecha_aceptacion: ordentrabajo.preventa_data?.fecha_aceptacion,
            creado_por: user?.username ?? '',
            fase_venta: ModulosEnumChoice.RECORDINADO,
            motivos: [
              {
                numero_referencia_motivo: motivoData.id_motivo,
                descripcion: motivoData.nombre,
                prioridad: motivoData.prioridad || 'ALTA',
                estado_motivo: EstadoMotivoEnum.NO_RESUELTO,
                modulo: motivoData.modulo,
                comentarios: [
                  {
                    comentario: textoComentario,
                    username: user?.username ?? '',
                    rol: user?.rol ?? '',
                    departamento: user?.departamento_nombre ?? 'TECNICO',
                    tipo: TipoParametroChoiceEnum.TEXTO,
                  },
                ],
              },
            ],
          };

          // URLs de las evidencias subidas a S3 (pueden estar vacías si el usuario
          // no subió esa evidencia). Se adjuntarán como comentarios separados con
          // tipo IMAGE vía enviarComentario después de que createVenta responda.
          const imageUrls = [
            fotoRechazoTecnico1Data.url,
            fotoRechazoTecnico2Data.url,
          ].filter((url): url is string => !!url);

          // Helper: hace getVentaById con reintentos para manejar eventual consistency
          // del chattest service (a veces tarda un instante en commitear el createVenta).
          const fetchVentaWithRetry = (
            attempts: number,
            onSuccess: (venta: IDataBitacoraRespById['data']) => void
          ) => {
            socket.emit(
              'getVentaById',
              { numero_referencia_venta: idPreagendamiento },
              (getResponse: IDataBitacoraRespById) => {
                const dm = getResponse?.data?.diccionario_motivo?.[0];
                const motivo = dm?.motivos?.[0];
                if (getResponse?.code === 200 && dm && motivo) {
                  onSuccess(getResponse.data);
                  return;
                }
                if (attempts > 1) {
                  // Reintentar tras un pequeño delay (eventual consistency)
                  setTimeout(() => fetchVentaWithRetry(attempts - 1, onSuccess), 350);
                }
              }
            );
          };

          // Emite comentario de texto + imágenes asociados a un motivo dado.
          // Se reutiliza tanto al agregar a una bitácora existente como tras crear una nueva.
          const emitirComentariosSobreMotivo = (
            numeroReferenciaVenta: string,
            diccionarioMotivoId: string,
            motivoId: string,
          ) => {
            socket.emit('enviarComentario', {
              numero_referencia_venta: numeroReferenciaVenta,
              diccionario_motivo_id: diccionarioMotivoId,
              motivo_id: motivoId,
              comentario_texto: textoComentario,
              username_create_venta: user?.username ?? '',
              tipo: TipoParametroChoiceEnum.TEXTO,
              departamento: user?.departamento_nombre ?? 'TECNICO',
              username: user?.username ?? '',
              rol: user?.rol ?? '',
            });
            imageUrls.forEach(url => {
              socket.emit('enviarComentario', {
                numero_referencia_venta: numeroReferenciaVenta,
                diccionario_motivo_id: diccionarioMotivoId,
                motivo_id: motivoId,
                comentario_texto: url,
                username_create_venta: user?.username ?? '',
                tipo: TipoParametroChoiceEnum.IMAGE,
                departamento: user?.departamento_nombre ?? 'TECNICO',
                username: user?.username ?? '',
                rol: user?.rol ?? '',
              });
            });
          };

          // Flujo original: crear nueva bitácora + adjuntar imágenes al motivo recién creado.
          const crearNuevaBitacora = () => {
            socket.emit(
              'createVenta',
              { ventaData },
              (createResponse: IDataBitacoraRespById) => {
                if (createResponse?.code !== 200) return;
                if (imageUrls.length === 0) return;
                setTimeout(() => {
                  fetchVentaWithRetry(3, venta => {
                    const dm = venta.diccionario_motivo?.[0];
                    const motivo = dm?.motivos?.[0];
                    if (!dm || !motivo) return;
                    imageUrls.forEach(url => {
                      socket.emit('enviarComentario', {
                        numero_referencia_venta: venta.numero_referencia_venta,
                        diccionario_motivo_id: dm._id,
                        motivo_id: motivo._id,
                        comentario_texto: url,
                        username_create_venta: user?.username ?? '',
                        tipo: TipoParametroChoiceEnum.IMAGE,
                        departamento: user?.departamento_nombre ?? 'TECNICO',
                        username: user?.username ?? '',
                        rol: user?.rol ?? '',
                      });
                    });
                  });
                }, 200);
              },
            );
          };

          // Busca bitácoras existentes del cliente antes de crear una nueva, para evitar
          // duplicar el registro en /operaciones/bitacora. Si existe al menos una bitácora
          // activa del cliente con motivo NO_RESUELTO, se anexa el comentario SOLO a la
          // más reciente (por updatedAt). Si no existe o todas están cerradas, se crea
          // una nueva (flujo original).
          const identificacion = ordentrabajo.prospecto_data?.identificacion;

          if (!identificacion) {
            crearNuevaBitacora();
          } else {
            let resuelto = false;
            const handler = (response: {
              code?: number;
              data?: { data?: Venta[] };
            }) => {
              if (resuelto) return;
              if (!response || response?.code !== 200) return;
              const ventas = response.data?.data;

              if (!Array.isArray(ventas)) {
                console.warn('Respuesta inválida de getVentas', response);
                return;
              }
              const identificacionNormalizada = String(identificacion).trim();

              const bitacorasCliente = ventas.filter(
                (venta: Venta) =>
                  String(venta.identificacion_cliente).trim() === identificacionNormalizada,
              );
              resuelto = true;
              socket.off('getVentas', handler);

              if (bitacorasCliente.length === 0) {
                crearNuevaBitacora();
                return;
              }

              // Elegir UNA sola bitácora: la más reciente con motivo abierto.
              const candidato = bitacorasCliente
                .filter(v => v.estado_bitacora === EstadoBitacoraEnum.EN_PROCESO)
                .map(v => {
                  const dm: DiccionarioMotivo | undefined =
                    v.diccionario_motivo?.find(d =>
                      d.motivos?.some(
                        m => m.estado_motivo === EstadoMotivoEnum.NO_RESUELTO,
                      ),
                    );
                  const motivo: Motivo | undefined = dm?.motivos.find(
                    m => m.estado_motivo === EstadoMotivoEnum.NO_RESUELTO,
                  );
                  return dm && motivo ? { venta: v, dm, motivo } : null;
                })
                .filter(
                  (x): x is { venta: Venta; dm: DiccionarioMotivo; motivo: Motivo } =>
                    x !== null,
                )
                .sort(
                  (a, b) =>
                    new Date(b.venta.updatedAt).getTime() -
                    new Date(a.venta.updatedAt).getTime(),
                )[0];

              // No había bitácora activa con motivo abierto: caemos al flujo original.
              if (!candidato) {
                crearNuevaBitacora();
                return;
              }

              emitirComentariosSobreMotivo(
                candidato.venta.numero_referencia_venta,
                candidato.dm._id,
                candidato.motivo._id,
              );
            };

            socket.on('getVentas', handler);
            socket.emit('getVentasFilter', {
              page: 1,
              pageSize: 100,
              filtros: { identificacion_cliente: identificacion },
            });
            // Red de seguridad: si chattest no responde, crear la bitácora igual.
            setTimeout(() => {
              if (!resuelto) {
                resuelto = true;
                socket.off('getVentas', handler);
                crearNuevaBitacora();
              }
            }, 3000);
          }
        }
      }

      setAlert(
        true,
        esFalla
          ? 'Orden de trabajo rechazada correctamente'
          : 'Orden de trabajo enviada a recoordinación correctamente',
        AlertSeverityEnum.success
      );
      navigate(returnUrlInstalacionesTecnicoPage, { replace: true });
    } catch {
      // Error already handled by mutation's onError
    }
    setIsGlobalLoading(false);
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Reportar novedad de Orden de trabajo"
      cancelTextBtn="NO"
      confirmTextBtn="SÍ"
      confirmVariantBtn="outlined"
      // actions
      onClose={handleResetForm}
      onConfirm={() => {
        formRechazo.handleSubmit(onSaveRejection, error => {
          console.log({ error });
          setAlert(
            true,
            'Todos los campos son requeridos para reportar la novedad',
            AlertSeverityEnum.error
          );
        })();
      }}
      // content
      minWidth="42%"
      contentNode={
        <>
          <Grid container spacing={3}>
            {/* -------- texto informativo -------- */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Para reportar una novedad en la orden de trabajo debe
                seleccionar el estado, categoría y subcategoría correspondiente,
                además de ingresar las evidencias. <br />
                ¿Está seguro de que desea reportar esta novedad?
              </Typography>
            </Grid>

            {/* -------- 1. Estado (Falla / Re coordinar) -------- */}
            <Grid item xs={12}>
              <TabTextLabel
                text="ESTADO"
                pt="0"
                pb="18px"
                pl="0"
                mt="0"
              />
              <CustomAutocomplete<EstadoOption>
                label="Estado"
                name="estado_informe"
                options={ESTADO_OPTIONS}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={formRechazo.getValues().estado_informe || ''}
                isLoadingData={false}
                control={formRechazo.control}
                error={formRechazo.formState.errors.estado_informe}
                helperText={
                  formRechazo.formState.errors.estado_informe?.message
                }
                onChangeValue={(value: string) => {
                  setSelectedEstado(value || '');
                  // reset dependientes
                  setSelectedCategoria('');
                  formRechazo.setValue('categoria_rechazo', '');
                  formRechazo.setValue('motivo_rechazo', '');
                }}
              />
            </Grid>

            {/* -------- 2. Categoría (Cliente / No Accesible) -------- */}
            {selectedEstado && (
              <Grid item xs={12}>
                <TabTextLabel
                  text="CATEGORÍA"
                  pt="0"
                  pb="18px"
                  pl="0"
                  mt="0"
                />
                <CustomAutocomplete<EstadoOption>
                  label="Categoría"
                  name="categoria_rechazo"
                  options={categoriaOptions}
                  valueKey="nombre"
                  actualValueKey="codigo"
                  defaultValue={
                    formRechazo.getValues().categoria_rechazo || ''
                  }
                  isLoadingData={false}
                  control={formRechazo.control}
                  error={formRechazo.formState.errors.categoria_rechazo}
                  helperText={
                    formRechazo.formState.errors.categoria_rechazo?.message
                  }
                  onChangeValue={(value: string) => {
                    setSelectedCategoria(value || '');
                    // reset subcategoría
                    formRechazo.setValue('motivo_rechazo', '');
                  }}
                />
              </Grid>
            )}

            {/* -------- 3. Subcategoría (motivo específico) -------- */}
            {selectedCategoria && (
              <Grid item xs={12}>
                <TabTextLabel
                  text="SUBCATEGORÍA"
                  pt="0"
                  pb="18px"
                  pl="0"
                  mt="0"
                />
                <CustomAutocomplete<MotivoRechazoOrdenTrabajo>
                  label="Subcategoría"
                  name="motivo_rechazo"
                  options={subcategoriaOptions}
                  valueKey="nombre"
                  actualValueKey="codigo"
                  defaultValue={formRechazo.getValues().motivo_rechazo || ''}
                  isLoadingData={isLoadingMotivosRechazoOT}
                  control={formRechazo.control}
                  error={formRechazo.formState.errors.motivo_rechazo}
                  helperText={
                    formRechazo.formState.errors.motivo_rechazo?.message
                  }
                />
              </Grid>
            )}

            {/* -------- descripción rechazo -------- */}
            <Grid item xs={12}>
              <TabTextLabel
                text="Descripción Rechazo"
                pt="12px"
                pb="18px"
                pl="0"
                mt="0"
              />
              <CustomTextArea
                label="Observación Rechazo"
                name="observacion_rechazo_tecnico"
                control={formRechazo.control}
                defaultValue={
                  formRechazo.getValues('observacion_rechazo_tecnico') || ''
                }
                error={
                  formRechazo.formState.errors.observacion_rechazo_tecnico
                }
                helperText={
                  formRechazo.formState.errors.observacion_rechazo_tecnico
                    ?.message
                }
                required={false}
              />
            </Grid>

            {/* -------- fotos -------- */}
            <Grid item xs={12}>
              <TabTextLabel
                text="Evidencias"
                pt="12px"
                pb="15px"
                pl="0"
                mt="0"
              />
              <Grid item xs={12} container spacing={3}>
                <Grid item {...gridSizeMdLg6}>
                  <UploadImageComponent
                    buttonText={'Evidencia 1'}
                    selectedImage={fotoRechazoTecnico1}
                    setSelectedImage={setFotoRechazoTecnico1}
                    idInput={'evidencia-1'}
                  />
                </Grid>
                <Grid item {...gridSizeMdLg6}>
                  <UploadImageComponent
                    buttonText={'Evidencia 2'}
                    selectedImage={fotoRechazoTecnico2}
                    setSelectedImage={setFotoRechazoTecnico2}
                    idInput={'evidencia-2'}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      }
    />
  );
};

export default RejectInstalacionAsigTecModal;
